import {Show} from 'platform/foundation';
import {v4 as uuidV4} from 'uuid';

import {useCallback, useRef} from 'react';
import {useWatch} from 'react-hook-form';
import {useDispatch} from 'react-redux';

import {useVehicleFormContext} from '@dms/features/vehicle-forms-kit';
import {
  Audit,
  AuditAssets,
  AuditDataAssetsV2Body,
  AuditFields,
  AuditVideoAssets,
  FormApi,
  getCondition,
  getInitialAuditData,
  getNewestAuditStructure,
  InspectionType,
  setAuditForCreate,
  TConditionForm,
  useApiDispatch,
  useThunkDispatch,
  VehicleCreateContext,
  searchCategoryByUniqueKey,
  AuditCategoryUniqueKey,
} from '@dms/teas';

import {useOnMount} from 'shared/web';

const categoriesToFind = [
  AuditCategoryUniqueKey.OVERALL_EVALUATION_OF_THE_EXTERIOR,
  AuditCategoryUniqueKey.OVERALL_EVALUATION_OF_THE_INTERIOR,
  AuditCategoryUniqueKey.OVERALL_EVALUATION_OF_THE_TECHNICAL_CONDITION,
] as const;

interface AuditWrapperProps {
  vehicleId?: string;
}

export const AuditWrapper = ({vehicleId}: AuditWrapperProps) => {
  const {control, auditContext, formApi, sessionId} = useVehicleFormContext();

  const features = useWatch({control, name: 'features'});

  const dispatch = useDispatch();
  const apiDispatch = useApiDispatch();
  const thunkDispatch = useThunkDispatch();

  const isMounted = useRef(false);

  const loadConditionStructureAndPrepareAudit = useCallback(
    async (sessionId: string | null) => {
      const formValues = formApi.getValues();

      let assets: AuditAssets = {};
      let fields: AuditFields = {};
      let videoAssets: AuditVideoAssets = {};
      let controlStructureId = null;

      const [structure] = await apiDispatch(getNewestAuditStructure.action, {
        inspectionType: InspectionType.vehicleCondition,
      });

      const categories = categoriesToFind
        .map((category) => searchCategoryByUniqueKey(structure?.categories ?? [], category))
        .filter(Boolean);

      auditContext.setStructure(categories);

      try {
        const vehicleIds = vehicleId ? [vehicleId] : [];
        const [condition] = await thunkDispatch(
          getCondition.action({
            requestBody: {vehicleIds},
            inspectionType: InspectionType.vehicleCondition,
            withoutReducer: true,
          })
        ).unwrap();

        const initData = getInitialAuditData(condition);

        assets = initData?.assets ?? {};
        fields = (formValues.auditData as AuditFields) ?? initData?.fields ?? {};
        videoAssets = initData?.videoAssets ?? {};
      } catch {
        controlStructureId = structure?.id;
      }

      dispatch(
        setAuditForCreate({
          id: sessionId ?? uuidV4(),
          assets,
          videoAssets,
          fields,
          isNew: true,
          controlStructureId,
        })
      );
      isMounted.current = true;
    },
    [apiDispatch, auditContext, dispatch, formApi, thunkDispatch, vehicleId]
  );

  useOnMount(() => {
    loadConditionStructureAndPrepareAudit(sessionId);
  });

  const setForm = useCallback(
    (form: FormApi<TConditionForm> | null) => {
      if (form) {
        auditContext.setForm(form);
      }
    },
    [auditContext]
  );

  const setAssets = useCallback(
    (assets: Record<string, AuditDataAssetsV2Body>) => {
      auditContext.setAssets(assets);
    },
    [auditContext]
  );

  return (
    <Show when={sessionId}>
      <VehicleCreateContext onConditionFormChange={setForm} onConditionAssetsChange={setAssets}>
        <Audit
          isCreate
          createVehicleFeatures={features ?? []}
          onValuesChange={(values) => {
            if (isMounted.current) {
              formApi.setValue(`auditData`, values);
            }
          }}
        />
      </VehicleCreateContext>
    </Show>
  );
};
